import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useViewMode } from '../App';
import { walletApi, earningsApi, campaignApi } from '../lib/api';
import { formatCurrency, getGreeting, formatRelativeTime, getTransactionStyle, getStatusStyle } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Eye, EyeOff, Copy, Plus, ArrowUpRight, ArrowDownLeft, 
  Receipt, Bell, ChevronRight, TrendingUp, Users, Target,
  Wallet, CreditCard, Activity
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_confusing-preview/artifacts/gu4q9ocf_trans_white_logo.png";

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isForcedMobile, isForcedDesktop } = useViewMode();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hideBalance, setHideBalance] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(3);
  const [naturalDesktop, setNaturalDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => setNaturalDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = isForcedDesktop ? true : isForcedMobile ? false : naturalDesktop;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, txRes, campaignsRes] = await Promise.all([
        walletApi.getWallet(),
        walletApi.getTransactions({ limit: isDesktop ? 10 : 7 }),
        campaignApi.getAll({ status: 'active' }),
      ]);
      
      setWallet(walletRes.data);
      setTransactions(txRes.data);
      setCampaigns(campaignsRes.data.slice(0, 3));
      
      // Generate chart data from transactions
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayTxs = txRes.data.filter(tx => {
          const txDate = new Date(tx.created_at);
          return txDate.toDateString() === date.toDateString();
        });
        last7Days.push({
          date: date.toLocaleDateString('en-US', { weekday: 'short' }),
          inflow: dayTxs.filter(t => t.direction === 'in').reduce((s, t) => s + t.amount, 0),
          outflow: dayTxs.filter(t => t.direction === 'out').reduce((s, t) => s + t.amount, 0),
        });
      }
      setChartData(last7Days);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const copyAccountNumber = () => {
    navigator.clipboard.writeText(wallet?.lekeewallet_id || '');
    toast.success('Copied to clipboard');
  };

  const QuickAction = ({ icon: Icon, label, onClick, testId }) => (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 group"
      data-testid={testId}
    >
      <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center group-hover:scale-105 transition-transform glow-primary">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <span className="text-sm text-muted-foreground group-hover:text-white transition-colors">
        {label}
      </span>
    </button>
  );

  // Loading state
  if (isLoading) {
    return (
      <AppLayout>
        <div className="pb-24 lg:pb-0">
          <div className="space-y-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-48 w-full rounded-xl" />
            <div className="flex justify-around lg:hidden">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="w-14 h-20 rounded-xl" />
              ))}
            </div>
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </div>
      </AppLayout>
    );
  }

  // Desktop Dashboard View
  if (isDesktop) {
    return (
      <AppLayout>
        <div className="space-y-6">
          {/* Welcome Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground">{getGreeting()},</p>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {wallet?.full_name?.split(' ')[0]}
              </h1>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">LekeeWallet ID</p>
              <button 
                onClick={copyAccountNumber}
                className="flex items-center gap-2 text-sm font-mono hover:text-primary transition-colors"
              >
                {wallet?.lekeewallet_id}
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Stats Cards Row */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <ArrowDownLeft className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-sm text-muted-foreground">Total Received</span>
              </div>
              <p className="text-2xl font-bold text-green-500">
                {formatCurrency(transactions.filter(t => t.direction === 'in').reduce((s, t) => s + t.amount, 0))}
              </p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5" />
                </div>
                <span className="text-sm text-muted-foreground">Total Sent</span>
              </div>
              <p className="text-2xl font-bold">
                {formatCurrency(transactions.filter(t => t.direction === 'out').reduce((s, t) => s + t.amount, 0))}
              </p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">Transactions</span>
              </div>
              <p className="text-2xl font-bold">{transactions.length}</p>
            </div>
            
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-yellow-500" />
                </div>
                <span className="text-sm text-muted-foreground">Active Campaigns</span>
              </div>
              <p className="text-2xl font-bold text-yellow-500">{campaigns.length}</p>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-3 gap-6">
            {/* Cash Flow Chart */}
            <div className="col-span-2 bg-card border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Cash Flow (Last 7 Days)
                </h2>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-muted-foreground">Inflow</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary" />
                    <span className="text-muted-foreground">Outflow</span>
                  </div>
                </div>
              </div>
              
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="outflowGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E02020" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#E02020" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a1a', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), '']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="inflow" 
                      stroke="#22C55E" 
                      strokeWidth={2}
                      fill="url(#inflowGradient)"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="outflow" 
                      stroke="#E02020" 
                      strokeWidth={2}
                      fill="url(#outflowGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Active Campaigns */}
            <div className="bg-card border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Active Campaigns
                </h2>
                <Link to="/earn" className="text-primary text-sm hover:underline">
                  View All
                </Link>
              </div>
              
              {campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">No active campaigns</p>
                  <Link to="/earn">
                    <Button size="sm" className="mt-3 bg-primary hover:bg-red-600">
                      Browse Campaigns
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((campaign) => {
                    const progress = ((campaign.budget_total - campaign.budget_remaining) / campaign.budget_total) * 100;
                    
                    return (
                      <Link
                        key={campaign.campaign_id}
                        to={`/campaigns/${campaign.campaign_id}`}
                        className="block p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-primary" />
                          </div>
                          <span className="font-medium text-sm truncate">{campaign.name}</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-2">
                          <div 
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-green-500">
                          {formatCurrency(campaign.budget_total - campaign.budget_remaining)} earned
                        </p>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recent Transactions
              </h2>
              <Link to="/transactions" className="text-primary text-sm hover:underline flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            {transactions.length === 0 ? (
              <div className="p-12 text-center">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">No transactions yet</p>
                <Button 
                  onClick={() => navigate('/receive')} 
                  className="mt-4 bg-primary hover:bg-red-600"
                >
                  Add Money
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Description</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.slice(0, 8).map((tx) => {
                      const style = getTransactionStyle(tx.type, tx.direction);
                      const statusStyle = getStatusStyle(tx.status);
                      const Icon = tx.direction === 'in' ? ArrowDownLeft : ArrowUpRight;
                      
                      return (
                        <tr 
                          key={tx.transaction_id} 
                          className="border-b border-white/10 hover:bg-white/5 transition-colors cursor-pointer"
                          onClick={() => navigate(`/transactions/${tx.transaction_id}`)}
                        >
                          <td className="p-4">
                            <div className={`w-10 h-10 rounded-full ${style.bgColor} flex items-center justify-center`}>
                              <Icon className={`w-5 h-5 ${style.color}`} />
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="font-medium">{tx.counterparty_name}</p>
                            <p className="text-sm text-muted-foreground">{tx.description}</p>
                          </td>
                          <td className={`p-4 font-medium ${tx.direction === 'in' ? 'text-green-500' : ''}`}>
                            {tx.direction === 'in' ? '+' : '-'}{formatCurrency(tx.amount)}
                          </td>
                          <td className="p-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                              {statusStyle.label}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {formatRelativeTime(tx.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Mobile View (existing)
  return (
    <AppLayout>
      <div className="pb-24">
        {/* Header */}
        <div className="bg-gradient-to-b from-primary/10 to-transparent">
          <div className="p-6 flex items-center justify-between">
            <img src={LOGO_URL} alt="LekeeWallet" className="h-8 w-auto" />
            <div className="flex items-center gap-4">
              <Link 
                to="/notifications" 
                className="relative p-2 hover:bg-white/5 rounded-full transition-colors"
                data-testid="notifications-btn"
              >
                <Bell className="w-6 h-6" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center font-medium">
                    {unreadNotifications}
                  </span>
                )}
              </Link>
              <Link to="/more" data-testid="profile-btn">
                {wallet?.avatar_url ? (
                  <img 
                    src={wallet.avatar_url} 
                    alt={wallet.full_name} 
                    className="w-10 h-10 rounded-full object-cover border-2 border-white/20"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold">
                    {wallet?.full_name?.charAt(0) || 'U'}
                  </div>
                )}
              </Link>
            </div>
          </div>
          
          {/* Greeting */}
          <div className="px-6 pb-4">
            <p className="text-muted-foreground">{getGreeting()},</p>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {wallet?.full_name?.split(' ')[0]}
            </h1>
          </div>
        </div>
        
        {/* Balance Card */}
        <div className="px-6 -mt-2">
          <div className="bg-card border border-white/10 rounded-2xl p-6 card-accent relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
            
            <div className="flex items-center justify-between mb-4">
              <span className="text-muted-foreground">Total Balance</span>
              <button 
                onClick={() => setHideBalance(!hideBalance)}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
                data-testid="toggle-balance-btn"
              >
                {hideBalance ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-4xl font-bold balance-text" data-testid="balance-amount">
                {hideBalance ? '₦•••••••' : formatCurrency(wallet?.available_balance)}
              </p>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Available: </span>
                <span className="text-green-500 font-medium">
                  {hideBalance ? '•••' : formatCurrency(wallet?.available_balance)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Pending: </span>
                <span className="text-yellow-500 font-medium">
                  {hideBalance ? '•••' : formatCurrency(wallet?.pending_balance)}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="px-6 py-6">
          <div className="flex items-center justify-around">
            <QuickAction 
              icon={Plus} 
              label="Add Money" 
              onClick={() => navigate('/receive')} 
              testId="quick-add-money"
            />
            <QuickAction 
              icon={ArrowUpRight} 
              label="Send" 
              onClick={() => navigate('/send')} 
              testId="quick-send"
            />
            <QuickAction 
              icon={ArrowDownLeft} 
              label="Withdraw" 
              onClick={() => navigate('/withdraw')} 
              testId="quick-withdraw"
            />
            <QuickAction 
              icon={Receipt} 
              label="Pay" 
              onClick={() => navigate('/payments')} 
              testId="quick-pay"
            />
          </div>
        </div>
        
        {/* Account Number */}
        <div className="px-6 pb-4">
          <div 
            className="bg-card/50 border border-white/10 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-card transition-colors"
            onClick={copyAccountNumber}
            data-testid="account-number-card"
          >
            <div>
              <p className="text-xs text-muted-foreground mb-1">LekeeWallet ID</p>
              <p className="font-mono font-medium">{wallet?.lekeewallet_id}</p>
            </div>
            <Copy className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
        
        {/* Active Campaigns (for creators) */}
        {wallet?.user_type === 'creator' && campaigns.length > 0 && (
          <div className="px-6 pb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Active Campaigns
              </h2>
              <Link to="/earn" className="text-primary text-sm font-medium flex items-center gap-1">
                See All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="flex gap-4 overflow-x-auto hide-scrollbar -mx-6 px-6">
              {campaigns.map((campaign) => (
                <Link
                  key={campaign.campaign_id}
                  to={`/campaigns/${campaign.campaign_id}`}
                  className="min-w-[280px] bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-colors"
                  data-testid={`campaign-card-${campaign.campaign_id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{campaign.name}</p>
                      <p className="text-xs text-muted-foreground">{campaign.objective}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${((campaign.budget_total - campaign.budget_remaining) / campaign.budget_total) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="text-green-500 font-medium">
                      {formatCurrency(campaign.budget_total - campaign.budget_remaining)} earned
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Recent Transactions */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Recent Activity
            </h2>
            <Link to="/transactions" className="text-primary text-sm font-medium flex items-center gap-1">
              See All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
            {transactions.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-white/5 flex items-center justify-center mb-4">
                  <Receipt className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">No transactions yet</p>
                <Button 
                  onClick={() => navigate('/receive')} 
                  className="mt-4 bg-primary hover:bg-red-600"
                >
                  Add Money
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {transactions.map((tx) => {
                  const style = getTransactionStyle(tx.type, tx.direction);
                  const statusStyle = getStatusStyle(tx.status);
                  const Icon = tx.direction === 'in' ? ArrowDownLeft : ArrowUpRight;
                  
                  return (
                    <Link
                      key={tx.transaction_id}
                      to={`/transactions/${tx.transaction_id}`}
                      className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors"
                      data-testid={`transaction-${tx.transaction_id}`}
                    >
                      <div className={`w-10 h-10 rounded-full ${style.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${style.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{tx.counterparty_name}</p>
                        <p className="text-sm text-muted-foreground">{formatRelativeTime(tx.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${tx.direction === 'in' ? 'text-green-500' : ''}`}>
                          {tx.direction === 'in' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                          {statusStyle.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      
    </AppLayout>
  );
};

export default HomePage;
